import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TrendingUp, ShoppingCart, UserPlus, Repeat } from "lucide-react";

import type { RootState, AppDispatch } from "../../../redux/store";
import { listenToBrandGrowth } from "../../../redux/storeOrdersSlice";

import {
  HEART, InsightsStyles, SectionHead, EmptyState, DeltaChip, money, compact,
} from "./StoreInsightsKit";

interface StoreGrowthProps { brandId: string }

/* a 30-slot spine so quiet days show as gaps rather than being
   silently dropped from the chart */
const spine = (daily: any[]) => {
  const byKey = new Map(daily.map((d) => [d.dateKey, d]));
  const out: { dateKey: string; revenue: number; orders: number }[] = [];
  for (let i = 29; i >= 0; i--) {
    const k = new Date(Date.now() - i * 86_400_000).toISOString().slice(0, 10);
    out.push(byKey.get(k) ?? { dateKey: k, revenue: 0, orders: 0 });
  }
  return out;
};

const StoreGrowth: React.FC<StoreGrowthProps> = ({ brandId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const g = useSelector((s: RootState) => (s as any).storeOrders?.brandGrowth ?? {});

  useEffect(() => {
    if (!brandId) return;
    const unsub = dispatch(listenToBrandGrowth(brandId) as any);
    return () => { if (typeof unsub === "function") unsub(); };
  }, [brandId, dispatch]);

  const days = useMemo(() => spine(g.daily ?? []), [g.daily]);
  const peak = useMemo(() => Math.max(1, ...days.map((d) => d.revenue)), [days]);
  const buyers = (g.newCustomers ?? 0) + (g.returningCustomers ?? 0);
  const newPct = buyers ? ((g.newCustomers ?? 0) / buyers) * 100 : 0;

  const cards = [
    {
      icon: <TrendingUp size={15} />, label: "Revenue", value: money(g.revenueNow),
      prev: `was ${money(g.revenuePrev)}`, pct: g.revenueChangePct ?? null, tone: HEART.green,
    },
    {
      icon: <ShoppingCart size={15} />, label: "Orders", value: compact(g.ordersNow),
      prev: `was ${compact(g.ordersPrev)}`, pct: g.ordersChangePct ?? null, tone: HEART.dark,
    },
  ];

  const nothingYet = (g.ordersNow ?? 0) === 0 && (g.ordersPrev ?? 0) === 0;

  return (
    <div className="si">
      <InsightsStyles />

      <SectionHead
        title="Growth"
        right={
          <span className="si-pill" style={{ background: HEART.blush, color: HEART.redDark }}>
            Last 30 days vs previous 30
          </span>
        }
      />

      {nothingYet ? (
        <EmptyState
          emoji="🌱"
          title="Not enough history yet"
          body="Once this store has a month of orders behind it, you'll see how each period compares to the last."
        />
      ) : (
        <>
          <div className="si-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", marginBottom: 16 }}>
            {cards.map((c, i) => (
              <div key={c.label} className="si-slab" style={{ padding: "16px 18px", animationDelay: `${i * 55}ms` }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 11, background: HEART.blush,
                    border: `2px solid ${HEART.dark}`, display: "flex", alignItems: "center",
                    justifyContent: "center", color: HEART.red,
                  }}>
                    {c.icon}
                  </div>
                  <DeltaChip pct={c.pct} />
                </div>
                <div className="si-num" style={{ fontSize: 26, color: c.tone }}>{c.value}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: HEART.textMuted, marginTop: 5 }}>{c.label}</div>
                <div style={{ fontSize: 10.5, color: HEART.textFaint, fontWeight: 600, marginTop: 2 }}>{c.prev}</div>
              </div>
            ))}
          </div>

          {/* daily revenue — 30 bars, one per day */}
          <div className="si-slab" style={{ padding: "16px 18px 18px", marginBottom: 16 }}>
            <p className="si-eyebrow">DAILY REVENUE</p>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 120, marginBottom: 8 }}>
              {days.map((d, i) => (
                <div
                  key={d.dateKey}
                  className="si-col"
                  title={`${d.dateKey} · ${money(d.revenue)} · ${d.orders} order${d.orders === 1 ? "" : "s"}`}
                  style={{
                    height: `${Math.max(2, (d.revenue / peak) * 100)}%`,
                    animationDelay: `${i * 14}ms`,
                    opacity: d.revenue ? 1 : 0.25,
                  }}
                />
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: HEART.textFaint, fontWeight: 700 }}>
              <span>30 days ago</span>
              <span>Peak day {money(peak)}</span>
              <span>Today</span>
            </div>
          </div>

          {/* who's buying — new against returning */}
          <div className="si-slab" style={{ padding: "16px 18px 18px" }}>
            <p className="si-eyebrow">WHO BOUGHT THIS PERIOD</p>

            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
              <span className="si-num" style={{ fontSize: 28 }}>{compact(buyers)}</span>
              <span style={{ fontSize: 12, color: HEART.textMuted, fontWeight: 600 }}>
                buyer{buyers === 1 ? "" : "s"}, {g.repeatRatePct ?? 0}% of them returning
              </span>
            </div>

            <div className="si-track" style={{ marginBottom: 12 }}>
              <div className="si-fill" style={{ width: `${newPct}%` }} />
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <span className="si-pill" style={{ background: HEART.redSoft, color: HEART.redDark }}>
                <UserPlus size={10} /> {compact(g.newCustomers)} new
              </span>
              <span className="si-pill" style={{ background: HEART.bg, color: HEART.textMuted }}>
                <Repeat size={10} /> {compact(g.returningCustomers)} returning
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default StoreGrowth;